import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { postCitaGrupal } from '../../services/General/citaServiceGraph';
import { fetchBuildings } from '../../services/Administracion/buildingsService';
import { Building } from '../../types/building';
import PersonaSearchSQL from '../shared/PersonaSearch';
import { PersonaSQL } from '../../types/PersonaSQL';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Importa el CSS de Quill si lo necesitas
import DOMPurify from "dompurify";
import Swal from 'sweetalert2';
import { formatDate, getCurrentDateTimeLocal, isDateNotInPast, getMinEndDate } from "../../utils/fechaUtil";
import CreatePersonaModal from '../shared/CrearPersonaModal';
import { getPersonaVetadaByCedula } from "../../services/General/personaVetadaService";
import { PersonaVetada } from "../../types/personaVetada";



interface PersonaSeleccionada extends PersonaSQL {
    email?: string;
}

const CitaGrupalForm = () => {
    const { user } = useAuth();
    const [form, setForm] = useState({
        host_email: user?.User.email || "",
        start_date: "",
        end_date: '',
        indications: '',
        office_location: '',
        building_id: null as number | null,
        cc_email: '',
        user: user?.User.email || "",
    });

    //Estado de la lista de personas
    const [personas, setPersonas] = useState<PersonaSeleccionada[]>([])
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [buildings, setBuildings] = useState<Building[]>([]);
    const [loadingBuildings, setLoadingBuildings] = useState(false);
    const [isContentValid, setIsContentValid] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    // Referencia para el contenedor de botones
    const footerRef = useRef<HTMLDivElement>(null);

    //Estados para PersonaSearch 
    const [showCreatePersonaModal, setShowCreatePersonaModal] = useState(false);
    const [pendingSearchQuery, setPendingSearchQuery] = useState('');
    const [personaSearchResetKey, setPersonaSearchResetKey] = useState(0);

    // Estados para validación de email del anfitrión
    const [emailErrors, setEmailErrors] = useState({
        hostEmail: false,
        ccEmail: false,
    });

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['clean']
        ],
    };

    const quillFormats = [
        'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
        'code-block', 'list', 'bullet'
    ];

    // Cargar edificios
    useEffect(() => {
        const loadBuildings = async () => {
            setLoadingBuildings(true);
            try {
                const edificios = await fetchBuildings();
                setBuildings(edificios.filter(b => b.is_active));
            } catch (error: any) {
                setError(error.message || 'Error al cargar edificios');
            } finally {
                setLoadingBuildings(false);
            }
        };
        loadBuildings();
    }, []);

    // Scroll automático al final cuando se agregan personas
    useEffect(() => {
        if (footerRef.current && personas.length > 0) {
            setTimeout(() => {
                footerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, 100);
        }
    }, [personas.length]);

    // Validar email
    const validateEmail = (email: string) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(String(email).toLowerCase());
    };

    // Manejar cambio de fecha
    const handleFechaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setForm(prev => ({ ...prev, start_date: newDate }));
    };

    const handleFechaFinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newEndDate = e.target.value;
        const startDate = new Date(form.start_date);
        const endDate = new Date(newEndDate);
        if (endDate.getTime() - startDate.getTime() < 3600000) {
            return;
        }
        setForm(prev => ({ ...prev, end_date: newEndDate }));
    };

    // Manejar contenido de indicaciones
    const handleContentChange = (content: string) => {
        const cleanContent = DOMPurify.sanitize(content, {
            ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'blockquote', 'code', 'h1', 'h2', 'h3'],
            ALLOWED_ATTR: ['href', 'target'],
        });
        setForm(prev => ({ ...prev, indications: cleanContent }));
        setIsContentValid(cleanContent.trim() === '' || cleanContent.length >= 20);
    };

    // Manejador para seleccionar persona
    const handlePersonaSelect = async (persona: PersonaSQL | null) => {
        if (!persona) return;

        // Verificar si ya está en la lista
        const yaExiste = personas.some(p => p.identificador === persona.identificador);
        if (yaExiste) {
            Swal.fire({
                title: 'Persona duplicada',
                text: `${persona.nombre} ${persona.apellido1} ya está en la lista`,
                icon: 'warning',
                timer: 2000,
                showConfirmButton: false
            });
            return;
        }

        // Verificar veto
        try {
            const personaVetada: PersonaVetada | null = await getPersonaVetadaByCedula(persona.identificador);
            if (personaVetada && personaVetada.veto_id && personaVetada.is_active) {
                Swal.fire({
                    title: '¡Persona Vetada!',
                    text: `${persona.nombre} ${persona.apellido1} está vetada. Motivo: ${personaVetada.motivo}`,
                    icon: 'error',
                    confirmButtonText: 'Entendido'
                });
                return;
            }
        } catch (error) {
            console.error('Error verificando veto:', error);
        }

        // Agregar a la lista
        const nombreCompleto = `${persona.nombre} ${persona.apellido1} ${persona.apellido2}`.trim();
        setPersonas(prev => [...prev, { ...persona, email: '' }]);

        // Resetear buscador
        setPersonaSearchResetKey(prev => prev + 1);

        Swal.fire({
            title: '¡Agregado!',
            text: `${nombreCompleto} ha sido agregado a la lista`,
            icon: 'success',
            toast: true,
            timer: 3000,
            showConfirmButton: false,
            position: 'bottom-right',
            timerProgressBar: true,
        });
    };

    // Eliminar persona de la lista
    const handleEliminarPersona = (index: number) => {
        const persona = personas[index];
        const nombreCompleto = `${persona.nombre} ${persona.apellido1}`;

        Swal.fire({
            title: '¿Eliminar?',
            text: `¿Desea eliminar a ${nombreCompleto} de la lista?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        }).then(result => {
            if (result.isConfirmed) {
                setPersonas(prev => prev.filter((_, i) => i !== index));
            }
        });
    };

    // Actualizar email de una persona
    const handleEmailChange = (index: number, email: string) => {
        setPersonas(prev => prev.map((p, i) =>
            i === index ? { ...p, email } : p
        ));
    };

    // Manejador cuando no se encuentra persona
    const handlePersonaNotFound = (query: string) => {
        setPendingSearchQuery(query);
        setShowCreatePersonaModal(true);
    };

    // Manejador cuando se crea persona
    const handlePersonaCreada = (nuevaPersona: PersonaSQL) => {
        const nombreCompleto = `${nuevaPersona.nombre} ${nuevaPersona.apellido1} ${nuevaPersona.apellido2}`.trim();
        setPersonas(prev => [...prev, { ...nuevaPersona, email: '' }]);
        setPersonaSearchResetKey(prev => prev + 1);

        Swal.fire({
            title: '¡Persona creada!',
            text: `${nombreCompleto} ha sido agregada al sistema y a la lista`,
            icon: 'success',
            timer: 2000
        });
        setShowCreatePersonaModal(false);
    };

    // Enviar formulario
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validaciones
        if (!form.start_date) {
            setError('Debe seleccionar fecha y hora');
            return;
        }

        if (!form.building_id) {
            setError('Debe seleccionar un edificio');
            return;
        }

        if (personas.length === 0) {
            setError('Debe agregar al menos una persona a la lista');
            return;
        }

        // Validar emails
        const emailInvalido = personas.some(p => p.email && !validateEmail(p.email));
        if (emailInvalido) {
            setError('Algunos emails no son válidos');
            return;
        }

        if (!validateEmail(form?.host_email)) {
            setEmailErrors(prev => ({ ...prev, hostEmail: true }));
            setError('El email del anfitrión no es válido');
            return;
        }

        if (!isDateNotInPast(form.start_date)) {
            setError('No se puede agendar una cita en el pasado');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const citaGrupalData = {
                ...form,
                start_date: format(new Date(form.start_date), 'yyyy-MM-dd HH:mm'),
                end_date: format(new Date(form.end_date), 'yyyy-MM-dd HH:mm'),
                building_id: form.building_id,
                personas: personas.map(p => ({
                    identificador: p.identificador,
                    tipo_identificacion: p.tipo_identificacion,
                    nombre: p.nombre,
                    apellido1: p.apellido1,
                    apellido2: p.apellido2,
                    email: p.email || ''
                })),
                user: user?.User.email || ''
            };

            const response = await postCitaGrupal(citaGrupalData);

            if (response.success === false) {
                throw new Error(response.message || 'Error al crear las citas grupales');
            }

            Swal.fire({
                title: '¡Éxito!',
                html: `
                    Se han creado <strong>${personas.length}</strong> citas exitosamente.<br>
                    Se han enviado los correos a cada invitado.<br>
                    El anfitrión ha recibido un resumen de todas las citas.
                `,
                icon: 'success',
                confirmButtonText: 'Aceptar'
            });

            // Resetear formulario
            setForm({
                host_email: user?.User.email || "",
                start_date: "",
                end_date: "",
                indications: "",
                office_location: "",
                building_id: null,
                cc_email: "",
                user: user?.User.email || "",
            });
            setPersonas([]);
            setResetKey(prev => prev + 1);

        } catch (error: any) {
            console.error('Error en cita grupal:', error);
            setError(error.message || 'Error al crear las citas');
        } finally {
            setLoading(false);
        }
    };

    // Helper para mostrar hora
    const getTimeDisplay = (dateString: string) => {
        if (!dateString) return '-';
        return format(new Date(dateString), 'HH:mm');
    };

    // Calcular altura máxima de la tabla basada en número de filas
    const getTableMaxHeight = () => {
        const rowHeight = 48; // Altura aproximada por fila
        const maxRows = 5; // Máximo de filas visibles antes de scroll
        return `${rowHeight * maxRows}px`;
    };

    return (
        <div className="container mt-3" style={{ paddingBottom: '100px' }}>
            <div className="card shadow-sm mb-4">
                <div className="card-header bg-primary text-white">
                    <h6 className="mb-0">
                        <i className="bi bi-people-fill me-2"></i>
                        Cita Grupal
                    </h6>
                    <small className="d-block text-white-50 mt-1">
                        Cree una cita para múltiples personas. Cada persona recibirá su propio correo y se generará un registro individual en el sistema.
                    </small>
                </div>
            </div>

            {success && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                    <i className="fas fa-check-circle me-2"></i>
                    {success}
                    <button type="button" className="btn-close" onClick={() => setSuccess(null)}></button>
                </div>
            )}

            <form onSubmit={handleSubmit}>
                {/* Datos comunes de la cita */}
                <div className="card shadow-sm mb-3">
                    <div className="card-header bg-light py-2">
                        <h6 className="mb-0">
                            <i className="bi bi-calendar-event me-2"></i>
                            Datos de la Cita
                        </h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-2">
                            <div className="col-md-6 mb-2">
                                <label className="form-label small">Anfitrión *</label>
                                <input
                                    type="email"
                                    className={`form-control form-control-sm ${emailErrors.hostEmail ? 'is-invalid' : ''}`}
                                    value={form.host_email}
                                    onChange={(e) => setForm({ ...form, host_email: e.target.value })}
                                    onBlur={() => setEmailErrors(prev => ({ ...prev, hostEmail: !validateEmail(form.host_email) }))}
                                    required
                                    readOnly
                                />
                                {emailErrors.hostEmail && <div className="invalid-feedback">Email no válido</div>}
                            </div>

                            <div className="col-md-6 mb-2">
                                <label className="form-label small">Edificio *</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={form.building_id?.toString() || ''}
                                    onChange={(e) => setForm({ ...form, building_id: e.target.value ? parseInt(e.target.value) : null })}
                                    required
                                >
                                    <option value="">Seleccionar edificio</option>
                                    {loadingBuildings ? (
                                        <option disabled>Cargando...</option>
                                    ) : (
                                        buildings.map(b => (
                                            <option key={b.building_id} value={b.building_id}>
                                                {b.building_name}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>
                        </div>
                        {/* Fila 2: Fecha y CC (en una sola fila) */}
                        <div className="row g-2">
                            <div className="col-md-3 mb-2">
                                <label className="form-label small">Fecha y Hora de Inicio *</label>
                                <input
                                    type="datetime-local"
                                    className="form-control form-control-sm"
                                    value={form.start_date}
                                    onChange={handleFechaChange}
                                    min={getCurrentDateTimeLocal()}
                                    required
                                />
                            </div>
                            <div className="col-md-3 mb-2">
                                <label className="form-label small">Fecha y Hora de Fin *</label>
                                <input
                                    type="datetime-local"
                                    className="form-control form-control-sm"
                                    value={form.end_date}
                                    onChange={handleFechaFinChange}
                                    min={form.start_date ? getMinEndDate(form.start_date) : ''}
                                    required
                                />
                            </div>
                            <div className="col-md-6 mb-2">
                                <label className="form-label small">Con Copia (opcional)</label>
                                <input
                                    type="email"
                                    className="form-control form-control-sm"
                                    value={form.cc_email}
                                    onChange={(e) => setForm({ ...form, cc_email: e.target.value })}
                                    placeholder="correo@ejemplo.com"
                                />
                                <small className="text-muted">Recibirá una copia del correo enviado al anfitrión</small>
                            </div>
                        </div>
                        {/* Indicaciones - ocupando todo el ancho */}
                        <div className="row">
                            <label className="form-label small">Indicaciones</label>
                            <ReactQuill
                                key={`quill-${resetKey}`}
                                theme="snow"
                                value={form.indications}
                                onChange={handleContentChange}
                                modules={quillModules}
                                formats={quillFormats}
                                placeholder="Indicaciones para los visitantes..."
                                className="bg-white"
                                style={{ height: "100px", marginBottom: "40px" }}
                            />
                            {!isContentValid && form.indications?.trim() !== '' && (
                                <small className="text-danger">El contenido debe tener al menos 20 caracteres</small>
                            )}

                        </div>
                    </div>
                </div>

                {/* Lista de Personas */}
                <div className="card shadow-sm mb-3">
                    <div className="card-header bg-light py-2 d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">
                            <i className="bi bi-person-plus-fill me-2"></i>
                            Personas a Invitar
                            <span className="badge bg-primary ms-2">{personas.length}</span>
                        </h6>
                    </div>
                    <div className="card-body p-2">
                        {/* Buscador */}
                        <div className="mb-2">
                            {/* <label className="form-label small">Agregar Persona</label> */}
                            <div className="d-flex gap-2 align-items-center">
                                <div className="flex-grow-1">
                                    <PersonaSearchSQL
                                        key={`persona-search-${personaSearchResetKey}`}
                                        onPersonaSelect={handlePersonaSelect}
                                        onPersonaNotFound={handlePersonaNotFound}
                                        value=""
                                    />
                                </div>
                            </div>
                            <small className="text-muted ms-3">Busque por cédula o nombre</small>
                        </div>

                        {/* Lista de personas */}
                        {personas.length === 0 ? (
                            <div className="alert alert-info text-center py-3">
                                <i className="bi bi-info-circle me-2"></i>
                                No hay personas agregadas. Use el buscador para agregar.
                            </div>
                        ) : (
                            <div className="table-responsive"
                                style={{
                                    maxHeight: getTableMaxHeight(),
                                    overflowY: 'auto',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px'
                                }}>
                                <table className="table table-sm table-hover mb-0">
                                    <thead className="table-light sticky-top" style={{ position: 'sticky', top: 0, zIndex:1 }}>
                                        <tr className='small'>
                                            <th>#</th>
                                            <th>Cédula</th>
                                            <th>Nombre Completo</th>
                                            <th>Email</th>
                                            <th className='text-center' style={{ width: '50px' }}><i className='bi bi-tools '></i></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {personas.map((persona, index) => (
                                            <tr key={`${persona.identificador}-${index}`} className='small'>
                                                <td>{index+1 }</td>
                                                <td className='py-1'>
                                                    <code>{persona.identificador}</code>
                                                </td>
                                                <td className='py-1'>
                                                    {persona.nombre} {persona.apellido1} {persona.apellido2}
                                                </td>
                                                <td className='py-1'>
                                                    <input
                                                        type="email"
                                                        className="form-control form-control-sm py-0"
                                                        value={persona.email || ''}
                                                        onChange={(e) => handleEmailChange(index, e.target.value)}
                                                        placeholder="email@ejemplo.com (opcional)"
                                                        style={{ fontSize: '0.75rem', height: '28px' }}
                                                    />
                                                </td>
                                                <td className="text-center py-1">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleEliminarPersona(index)}
                                                        title="Eliminar" style={{ fontSize: '0.7rem' }}
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {/* Resumen */}
                        {personas.length > 0 && (
                            <div className="alert alert-secondary mt-2 py-1 mb-0">
                                <small>
                                    <i className="bi bi-envelope-paper me-1"></i>
                                    <strong>{personas.length}</strong> persona(s) seleccionada(s).
                                    Cada una recibirá su propio correo de invitación.
                                </small>
                            </div>
                        )}
                    </div>
                </div>

                {error && <div className="alert alert-danger mt-2 py-2">{error}</div>}

                <div ref={footerRef} className="d-flex gap-2 justify-content-end mt-3 mb-5">
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                            setForm({
                                host_email: user?.User.email || "",
                                start_date: "",
                                end_date: "",
                                indications: "",
                                office_location: "",
                                building_id: null,
                                cc_email: "",
                                user: user?.User.email || "",
                            });
                            setPersonas([]);
                            setResetKey(prev => prev + 1);
                        }}
                    >
                        <i className="bi bi-eraser me-2"></i>
                        Limpiar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary btn-sm"
                        disabled={loading || personas.length === 0}
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Creando {personas.length} cita(s)...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-calendar-plus me-1"></i>
                                Crear {personas.length} Cita(s)
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Modal para crear nueva persona */}
            <CreatePersonaModal
                show={showCreatePersonaModal}
                onHide={() => setShowCreatePersonaModal(false)}
                searchQuery={pendingSearchQuery}
                onPersonaCreada={handlePersonaCreada}
            />
        </div>
    );
};

export default CitaGrupalForm;